import { getJson, pathSegment, postJson } from "./client";

/**
 * Billing: a person's plan, what it allows, and what is used and left.
 * Amounts that can be fractional come as decimal strings and are read
 * with {@link amount}.
 *
 * The service is the platform's (`platform_billing_api`, in
 * platform/crates), which answers in jobenvoy's catalog's words: a
 * tier name, each entitlement's values, each limit's count. The claims and
 * the plans are read here into this app's own shapes, as the services do
 * in `api::plan`.
 */
export const BILLING_BASE = "/v1/billing";

export type BillingTier = "free" | "pro" | "max";
export type Meter = "tailors" | "resume_imports";

export interface MeterUsage {
    meter: Meter;
    limit: string;
    used: string;
    remaining: string;
    credits: string;
    credits_used: string;
    credits_remaining: string;
}

export interface UsageReport {
    period_start: string;
    period_end: string;
    meters: MeterUsage[];
}

export interface BillingClaims {
    entitlements: { tier: BillingTier; extra_models: string[] };
    limits: { tailors_per_month: number; tailor_credits: number; resume_imports_per_month: number };
    onboarding: boolean;
    period?: { start: string; end: string } | null;
}

/** How often a price bills; `quarter` is every three months. */
export type BillingInterval = "month" | "quarter" | "year";

export interface BillingPlanPrice {
    interval: BillingInterval;
    unit_amount: number;
    currency: string;
}

export interface BillingPlan {
    tier: BillingTier;
    name: string;
    tailors_per_month: number;
    prices: BillingPlanPrice[];
}

export interface BillingPackPrice {
    tailors: number;
    unit_amount: number;
    currency: string;
}

export interface BillingPlanList {
    plans: BillingPlan[];
    packs: BillingPackPrice[];
}

/** A decimal string as billing writes it, never below zero; 0 when it is not one. */
export function amount(value: string | null | undefined): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

const userPath = (userId: string, rest: string) =>
    `${BILLING_BASE}/users/${pathSegment(userId)}/${rest}`;

export async function getUsage(userId: string): Promise<UsageReport> {
    return getJson(userPath(userId, "usage"));
}

/** `platform_billing_api::claims::Claims`, as billing answers. */
export interface WireClaims {
    tier: string;
    entitlements?: Record<string, string[]>;
    limits?: Record<string, number>;
    onboarding?: boolean;
    period?: { start: string; end: string } | null;
}

const TIERS: readonly BillingTier[] = ["free", "pro", "max"];

/** A tier this app does not have is Free: nothing is granted that is not named. */
function tierOf(value: unknown): BillingTier {
    return TIERS.find((tier) => tier === value) ?? "free";
}

export function claimsFromWire(wire: WireClaims): BillingClaims {
    const limit = (key: string) => wire.limits?.[key] ?? 0;
    return {
        entitlements: {
            tier: tierOf(wire.tier),
            extra_models: wire.entitlements?.extra_model ?? [],
        },
        limits: {
            tailors_per_month: limit("tailors_per_month"),
            tailor_credits: limit("tailor_credits"),
            resume_imports_per_month: limit("resume_imports_per_month"),
        },
        onboarding: wire.onboarding ?? false,
        period: wire.period ?? null,
    };
}

export async function getClaims(userId: string): Promise<BillingClaims> {
    return claimsFromWire(await getJson<WireClaims>(userPath(userId, "claims")));
}

/** `platform_billing_api::catalog::PlanList`, as billing answers. */
export interface WirePlanList {
    plans: {
        tier: string;
        name: string;
        limits?: Record<string, number>;
        prices: BillingPlanPrice[];
    }[];
    packs?: { meter: string; credits: number; unit_amount: number; currency: string }[];
}

export function plansFromWire(wire: WirePlanList): BillingPlanList {
    return {
        plans: wire.plans.map((plan) => ({
            tier: tierOf(plan.tier),
            name: plan.name,
            tailors_per_month: plan.limits?.tailors_per_month ?? 0,
            prices: plan.prices,
        })),
        packs: (wire.packs ?? [])
            .filter((pack) => pack.meter === "tailors")
            .map((pack) => ({
                tailors: pack.credits,
                unit_amount: pack.unit_amount,
                currency: pack.currency,
            })),
    };
}

export async function getPlans(): Promise<BillingPlanList> {
    return plansFromWire(await getJson<WirePlanList>(`${BILLING_BASE}/plans`));
}

/**
 * A Stripe customer portal session: invoices, the card, and on a paid
 * plan, moving between plans. On Free it moves no plan: the move off Free
 * is {@link openPlanCheckout}.
 */
export async function openPortal(userId: string, returnUrl: string): Promise<{ url: string }> {
    return postJson(userPath(userId, "portal-session"), { return_url: returnUrl });
}

/**
 * A Stripe Checkout session to start Pro or Max from Free, billed every
 * `interval`. Checkout asks for the billing address and adds the tax.
 * Free only: a paid plan moves in the portal.
 */
export async function openPlanCheckout(
    userId: string,
    tier: Exclude<BillingTier, "free">,
    interval: BillingInterval,
    successUrl: string,
    cancelUrl: string,
): Promise<{ url: string }> {
    return postJson(userPath(userId, "plan-checkout"), {
        tier,
        interval,
        success_url: successUrl,
        cancel_url: cancelUrl,
    });
}

/** A Stripe Checkout session for one tailor pack, named by its size. Pro and Max only. */
export async function openPackCheckout(
    userId: string,
    pack: number,
    successUrl: string,
    cancelUrl: string,
): Promise<{ url: string }> {
    return postJson(userPath(userId, "pack-checkout"), {
        meter: "tailors",
        credits: pack,
        success_url: successUrl,
        cancel_url: cancelUrl,
    });
}
